import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, Camera, CheckCircle2, Send, ShieldCheck, Wrench, Facebook, Instagram, Globe } from "lucide-react";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { getAllWilayaNames, getCommunesForWilaya } from "@/hooks/useAlgeriaData";
import { ARTISAN_SPECIALITES } from "@/lib/constants";
import HandymanSpecialtiesSelector from "@/components/HandymanSpecialtiesSelector";
import type { HandymanSpecialtySelection } from "@/lib/handymanConfig";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate, useLocalizedPath } from "@/hooks/useLocalizedNavigate";

// Professionnel : toutes les spécialités sauf "tous" et "Artisan polyvalent"
// (Artisan polyvalent est réservé aux indépendants)
const specialitesPro = ARTISAN_SPECIALITES.filter(
  (s) => s.value !== "tous" && s.value !== "Artisan polyvalent"
);

// Indépendant : toutes les spécialités sauf "tous" et "Entreprise tout corps d'état"
// (inclut "Artisan polyvalent" comme choix possible)
const specialitesInd = ARTISAN_SPECIALITES.filter(
  (s) => s.value !== "tous" && s.value !== "Entreprise tout corps d'état"
);

const TCE_METIERS = ["Plomberie", "Électricité", "Maçonnerie", "Carrelage", "Peinture", "Menuiserie", "Serrurerie", "Climatisation"];

const Inscription = () => {
  const { t, locale } = useLanguage("auth");
  const wilayas = getAllWilayaNames(locale);
  const lNavigate = useLocalizedNavigate();
  const lp = useLocalizedPath();

  useSEO({
    title: t("inscription_artisan.title"),
    description: locale === "ar"
      ? "أنشئ ملفك كحرفي على حرافي في 5 دقائق. تمكّن مجاناً من الوصول إلى طلبات الأفراد في جميع ولايات الجزائر. 0٪ عمولة."
      : "Créez votre profil artisan sur Hirafi en 5 minutes. Accédez gratuitement aux demandes de particuliers dans toutes les wilayas d'Algérie. 0% de commission.",
    canonical: "/inscription",
    locale,
  });

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("independant");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [phone, setPhone] = useState("");
  const [description, setDescription] = useState("");
  const [specialite, setSpecialite] = useState("Artisan polyvalent");
  const [wilaya, setWilaya] = useState("");
  const [commune, setCommune] = useState("");
  const [handymanSpecialties, setHandymanSpecialties] = useState<HandymanSpecialtySelection[]>([]);
  const [raisonSociale, setRaisonSociale] = useState("");
  const [nif, setNif] = useState("");
  const [numeroRC, setNumeroRC] = useState("");
  const [adresse, setAdresse] = useState("");
  const [emailEntreprise, setEmailEntreprise] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");

  const isPro = activeTab === "professionnel";
  const isEntrepriseTCE = specialite === "Entreprise tout corps d'état";

  // Label "(optionnel)"
  const Opt = () => (
    <span className="font-normal text-muted-foreground text-xs">{t("inscription_artisan.opt")}</span>
  );

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setWilaya("");
    setCommune("");
    setHandymanSpecialties([]);
    if (tab === "independant") {
      setSpecialite("Artisan polyvalent");
    } else {
      setSpecialite("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t("inscription_artisan.error_passwords"));
      return;
    }
    if (password.length < 6) {
      toast.error(t("inscription_artisan.error_too_short"));
      return;
    }
    if (!isPro && handymanSpecialties.length === 0) {
      toast.error(t("inscription_artisan.error_competences"));
      return;
    }
    if (isPro && !specialite) {
      toast.error(t("inscription_artisan.error_specialite"));
      return;
    }
    if (specialite === "Artisan polyvalent") {
      if (handymanSpecialties.length === 0) {
        toast.error(t("inscription_artisan.error_specialite_handyman"));
        return;
      }
      const otherSelected = handymanSpecialties.find((s) => s.specialty_code === "other");
      if (otherSelected && !otherSelected.other_specialty_label?.trim()) {
        toast.error(t("inscription_artisan.error_other_specialty"));
        return;
      }
    }
    if (!wilaya) {
      toast.error(t("inscription_artisan.error_wilaya"));
      return;
    }
    if (isPro && (!raisonSociale.trim() || !nif.trim())) {
      toast.error(t("inscription_artisan.error_pro_required"));
      return;
    }
    if (!isPro && !nom.trim()) {
      toast.error(t("inscription_artisan.error_nom"));
      return;
    }

    setLoading(true);

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: prenom,
            last_name: nom,
            artisan_type: activeTab === "independant" ? "bricoleur" : "entreprise",
            specialite,
            wilaya,
            commune,
            raison_sociale: isPro ? raisonSociale : "",
            nif: isPro ? nif : "",
            numero_rc: isPro ? numeroRC : "",
            adresse: isPro ? adresse : "",
            email_entreprise: isPro ? emailEntreprise : "",
            facebook_url: facebookUrl || "",
            instagram_url: instagramUrl || "",
            website_url: websiteUrl || "",
            phone: phone || "",
            description: description || "",
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error("Erreur lors de la création du compte");

      if (specialite === "Artisan polyvalent" && handymanSpecialties.length > 0 && authData.user) {
        await new Promise((r) => setTimeout(r, 1500));
        const { data: artisanProfile } = await supabase
          .from("artisan_profiles")
          .select("id")
          .eq("user_id", authData.user.id)
          .maybeSingle();

        if (artisanProfile?.id) {
          const rows = handymanSpecialties.map((s) => ({
            artisan_id: artisanProfile.id,
            specialty_code: s.specialty_code,
            other_specialty_label:
              s.specialty_code === "other" ? (s.other_specialty_label ?? null) : null,
          }));
          await supabase.from("artisan_handyman_specialties").insert(rows);
        }
      }

      if (logoFile && isPro) {
        const userId = authData.user.id;
        const ext = logoFile.name.split(".").pop();
        const path = `${userId}/logo.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from("logos")
          .upload(path, logoFile, { upsert: true });

        if (uploadError) {
          toast.warning(t("inscription_artisan.warning_logo"));
        } else {
          const { data: urlData } = supabase.storage.from("logos").getPublicUrl(path);
          await new Promise((r) => setTimeout(r, 1000));
          const { error: updateError } = await supabase
            .from("artisan_profiles")
            .update({ logo_url: urlData.publicUrl })
            .eq("user_id", userId);
          if (updateError) {
            toast.warning(t("inscription_artisan.warning_logo_url"));
          }
        }
      }

      toast.success(t("inscription_artisan.success"));
      lNavigate("/connexion");
    } catch (error: any) {
      toast.error(error.message || t("inscription_artisan.error_passwords"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container max-w-2xl py-10 pb-20 md:pb-10">
        <div className="mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-primary mb-1.5">{t("inscription_artisan.join")}</p>
          <h1 className="font-display text-2xl font-bold md:text-3xl">{t("inscription_artisan.title")}</h1>
          <p className="mt-1.5 text-muted-foreground">{t("inscription_artisan.subtitle")}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 h-12">
              <TabsTrigger value="independant" className="gap-2 text-sm font-medium">
                <Wrench className="h-4 w-4" />
                {t("inscription_artisan.tab_independant")}
              </TabsTrigger>
              <TabsTrigger value="professionnel" className="gap-2 text-sm font-medium">
                <ShieldCheck className="h-4 w-4" />
                {t("inscription_artisan.tab_professionnel")}
              </TabsTrigger>
            </TabsList>

            {/* Section connexion — commun aux deux onglets */}
            <div className="rounded-xl border bg-card shadow-warm-sm p-6 space-y-5">
              <h2 className="font-display text-base font-semibold flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full gradient-hero text-white text-xs font-bold">1</span>
                {t("inscription_artisan.section_connexion")}
              </h2>

              <GoogleAuthButton
                label={t("google.sign_up")}
                onBeforeRedirect={() => {
                  localStorage.setItem(
                    "hirafi_google_artisan_type",
                    activeTab === "independant" ? "bricoleur" : "entreprise"
                  );
                }}
              />
              <div className="relative flex items-center gap-3">
                <div className="flex-1 border-t border-border" />
                <span className="text-xs text-muted-foreground">{t("inscription_artisan.or_email")}</span>
                <div className="flex-1 border-t border-border" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="reg-email" className="font-display font-semibold text-sm">{t("inscription_client.email_label")}</Label>
                <Input id="reg-email" type="email" placeholder="votre@email.dz" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="reg-password" className="font-display font-semibold text-sm">{t("inscription_client.password_label")}</Label>
                  <Input id="reg-password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-confirm" className="font-display font-semibold text-sm">{t("inscription_client.confirm_label")}</Label>
                  <Input id="reg-confirm" type="password" placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="h-11" />
                </div>
              </div>
            </div>

            {/* ── Indépendant ── */}
            <TabsContent value="independant" className="mt-0">
              <div className="rounded-xl border bg-card shadow-warm-sm p-6 space-y-6">
                <h2 className="font-display text-base font-semibold flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full gradient-hero text-white text-xs font-bold">2</span>
                  {t("inscription_artisan.section_personal")}
                </h2>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.nom")}</Label>
                    <Input placeholder="Nom" value={nom} onChange={(e) => setNom(e.target.value)} className="h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.prenom")} <Opt /></Label>
                    <Input placeholder="Prénom" value={prenom} onChange={(e) => setPrenom(e.target.value)} className="h-11" />
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.competences_title")}</Label>
                    <p className="mt-0.5 text-xs text-muted-foreground">{t("inscription_artisan.competences_desc")}</p>
                  </div>
                  <HandymanSpecialtiesSelector value={handymanSpecialties} onChange={setHandymanSpecialties} />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.wilaya")}</Label>
                    <Select value={wilaya} onValueChange={(v) => { setWilaya(v); setCommune(""); }}>
                      <SelectTrigger className="h-11"><SelectValue placeholder={t("inscription_artisan.select")} /></SelectTrigger>
                      <SelectContent>{wilayas.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.commune")} <Opt /></Label>
                    <Select value={commune} onValueChange={setCommune} disabled={!wilaya}>
                      <SelectTrigger className="h-11"><SelectValue placeholder={wilaya ? t("inscription_artisan.select") : t("inscription_artisan.commune_first")} /></SelectTrigger>
                      <SelectContent>{getCommunesForWilaya(wilaya, locale).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="font-display font-semibold text-sm">{t("inscription_artisan.phone")} <Opt /></Label>
                  <Input type="tel" placeholder="Ex: 0555 12 34 56" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-11" />
                </div>

                <div className="space-y-3">
                  <Label className="font-display font-semibold text-sm">{t("inscription_artisan.social")} <Opt /></Label>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Facebook className="h-5 w-5 shrink-0 text-muted-foreground" />
                      <Input placeholder={t("inscription_artisan.facebook_placeholder")} value={facebookUrl} onChange={(e) => setFacebookUrl(e.target.value)} />
                    </div>
                    <div className="flex items-center gap-2">
                      <Instagram className="h-5 w-5 shrink-0 text-muted-foreground" />
                      <Input placeholder={t("inscription_artisan.instagram_placeholder")} value={instagramUrl} onChange={(e) => setInstagramUrl(e.target.value)} />
                    </div>
                    <div className="flex items-center gap-2">
                      <Globe className="h-5 w-5 shrink-0 text-muted-foreground" />
                      <Input placeholder={t("inscription_artisan.website_placeholder")} value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} />
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="font-display font-semibold text-sm">{t("inscription_artisan.description")} <Opt /></Label>
                  <Textarea
                    placeholder={t("inscription_artisan.description_placeholder_ind")}
                    className="min-h-[100px]"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <Button variant="hero" size="xl" className="w-full" type="submit" disabled={loading}>
                  <Send className="me-2 h-5 w-5" />
                  {loading ? t("inscription_artisan.loading") : t("inscription_artisan.submit")}
                </Button>
              </div>
            </TabsContent>

            {/* ── Professionnel ── */}
            <TabsContent value="professionnel" className="mt-0">
              <div className="rounded-xl border bg-card shadow-warm-sm p-6 space-y-6">
                <h2 className="font-display text-base font-semibold flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full gradient-hero text-white text-xs font-bold">2</span>
                  {t("inscription_artisan.section_entreprise")}
                </h2>

                <div className="space-y-2">
                  <Label className="font-display font-semibold text-sm">{t("inscription_artisan.logo")} <Opt /></Label>
                  <div className="flex items-center gap-4">
                    <label
                      htmlFor="logo-upload"
                      className="flex h-20 w-20 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 transition-colors hover:border-primary/50"
                    >
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo" className="h-full w-full object-cover" />
                      ) : (
                        <Camera className="h-6 w-6 text-primary" />
                      )}
                    </label>
                    <input id="logo-upload" type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                    <p className="text-sm text-muted-foreground">{t("inscription_artisan.logo_hint")}</p>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <div className="space-y-1.5">
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.raison_sociale")}</Label>
                    <Input placeholder={t("inscription_artisan.raison_sociale_placeholder")} value={raisonSociale} onChange={(e) => setRaisonSociale(e.target.value)} className="h-11 bg-background" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.nif")}</Label>
                    <Input placeholder={t("inscription_artisan.nif_placeholder")} value={nif} onChange={(e) => setNif(e.target.value)} className="h-11 bg-background" />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.rc")} <Opt /></Label>
                    <Input placeholder={t("inscription_artisan.rc_placeholder")} value={numeroRC} onChange={(e) => setNumeroRC(e.target.value)} className="h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.adresse")} <Opt /></Label>
                    <Input placeholder={t("inscription_artisan.adresse_placeholder")} value={adresse} onChange={(e) => setAdresse(e.target.value)} className="h-11" />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.gerant_nom")} <Opt /></Label>
                    <Input placeholder={t("inscription_artisan.gerant_nom_placeholder")} value={nom} onChange={(e) => setNom(e.target.value)} className="h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.gerant_prenom")} <Opt /></Label>
                    <Input placeholder={t("inscription_artisan.gerant_prenom_placeholder")} value={prenom} onChange={(e) => setPrenom(e.target.value)} className="h-11" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="font-display font-semibold text-sm">{t("inscription_artisan.specialite")}</Label>
                  <Select value={specialite} onValueChange={(v) => { setSpecialite(v); setHandymanSpecialties([]); }}>
                    <SelectTrigger className="h-11"><SelectValue placeholder={t("inscription_artisan.specialite_placeholder")} /></SelectTrigger>
                    <SelectContent>
                      {specialitesPro.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {locale === "ar" ? s.label_ar : s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Bandeau explicatif — Entreprise tout corps d'état */}
                {isEntrepriseTCE && (
                  <div className="rounded-xl border border-primary/25 bg-primary/5 p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                      <div>
                        <p className="text-sm font-semibold text-foreground">Entreprise tout corps d'état</p>
                        <p className="mt-0.5 text-sm text-muted-foreground">
                          Entreprise capable de gérer plusieurs corps de métier et des projets complets de rénovation.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 ps-8">
                      {TCE_METIERS.map((m) => (
                        <span
                          key={m}
                          className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-background px-2.5 py-0.5 text-xs text-primary"
                        >
                          <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {specialite === "Artisan polyvalent" && (
                  <div className="space-y-2">
                    <div>
                      <Label className="font-display font-semibold text-sm">{t("inscription_artisan.competences_pro")}</Label>
                      <p className="mt-0.5 text-xs text-muted-foreground">{t("inscription_artisan.competences_pro_desc")}</p>
                    </div>
                    <HandymanSpecialtiesSelector value={handymanSpecialties} onChange={setHandymanSpecialties} />
                  </div>
                )}

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.wilaya")}</Label>
                    <Select value={wilaya} onValueChange={(v) => { setWilaya(v); setCommune(""); }}>
                      <SelectTrigger className="h-11"><SelectValue placeholder={t("inscription_artisan.select")} /></SelectTrigger>
                      <SelectContent>{wilayas.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.commune")} <Opt /></Label>
                    <Select value={commune} onValueChange={setCommune} disabled={!wilaya}>
                      <SelectTrigger className="h-11"><SelectValue placeholder={wilaya ? t("inscription_artisan.select") : t("inscription_artisan.commune_first")} /></SelectTrigger>
                      <SelectContent>{getCommunesForWilaya(wilaya, locale).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.phone")} <Opt /></Label>
                    <Input type="tel" placeholder="Ex: 0555 12 34 56" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-display font-semibold text-sm">{t("inscription_artisan.phone_entreprise")} <Opt /></Label>
                    <Input type="email" placeholder={t("inscription_artisan.phone_entreprise_placeholder")} value={emailEntreprise} onChange={(e) => setEmailEntreprise(e.target.value)} className="h-11" />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="font-display font-semibold text-sm">{t("inscription_artisan.social")} <Opt /></Label>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Facebook className="h-5 w-5 shrink-0 text-muted-foreground" />
                      <Input placeholder={t("inscription_artisan.facebook_placeholder")} value={facebookUrl} onChange={(e) => setFacebookUrl(e.target.value)} />
                    </div>
                    <div className="flex items-center gap-2">
                      <Instagram className="h-5 w-5 shrink-0 text-muted-foreground" />
                      <Input placeholder={t("inscription_artisan.instagram_placeholder")} value={instagramUrl} onChange={(e) => setInstagramUrl(e.target.value)} />
                    </div>
                    <div className="flex items-center gap-2">
                      <Globe className="h-5 w-5 shrink-0 text-muted-foreground" />
                      <Input placeholder={t("inscription_artisan.website_placeholder")} value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} />
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="font-display font-semibold text-sm">{t("inscription_artisan.description")} <Opt /></Label>
                  <Textarea
                    placeholder={t("inscription_artisan.description_placeholder_pro")}
                    className="min-h-[100px]"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <Button variant="hero" size="xl" className="w-full" type="submit" disabled={loading}>
                  <Send className="me-2 h-5 w-5" />
                  {loading ? t("inscription_artisan.loading") : t("inscription_artisan.submit")}
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {t("inscription_artisan.already_registered")}{" "}
            <Link to={lp("/connexion")} className="font-semibold text-primary hover:underline underline-offset-4">
              {t("inscription_artisan.login")}
            </Link>
          </p>
        </form>
      </main>
      <Footer />
    </div>
  );
};

export default Inscription;
